// Deferred QuickBooks push for a confirmed Receipt Scan (docs/RECEIPT_SCAN_MODULE.md) — lets a QA
// user confirm receiving logs without QuickBooks access, and an admin push that same receipt to
// QBO later as a separate action. Triggered from a badge on any IngredientReceivingLog row that
// shares the same originating scan; several rows can share one scan, so this always operates at
// the scan (whole-receipt) level, not per-row.
import React, { useState, useEffect, useMemo } from 'react';
import receiptScanService from '../services/receiptScanService';
import qboService from '../services/qboService';
import companyService from '../services/companyService';
import SearchableSelect from './SearchableSelect';
import { usePermissions } from '../context/PermissionContext';

export default function PushReceiptToQboModal({ scanId, onClose, onPushed }) {
  const { companySettings, refresh: refreshPermissions } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scan, setScan] = useState(null);
  const [ingredientAccounts, setIngredientAccounts] = useState([]); // [{id, name, qbExpenseAccountId, qbExpenseAccountName}]
  const [company, setCompany] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [savingKey, setSavingKey] = useState(null); // which inline "Save" button is in flight

  const [bankAccounts, setBankAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [qbPurchaseAccountId, setQbPurchaseAccountId] = useState('');
  const [overrides, setOverrides] = useState({}); // { [lineIndex]: qbExpenseAccountId } — non-ingredient lines
  const [ingredientOverrides, setIngredientOverrides] = useState({}); // { [ingredientId]: qbExpenseAccountId }
  const [defaultAccountOverride, setDefaultAccountOverride] = useState('');
  const [salesTaxOverride, setSalesTaxOverride] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [scanData, banks, expenses] = await Promise.all([
        receiptScanService.getScan(scanId),
        qboService.getAccounts(['Bank', 'Credit Card']),
        qboService.getAccounts(),
      ]);
      setScan(scanData);
      setIngredientAccounts(scanData.ingredientAccounts || []);
      setCompany(scanData.company || null);
      setBankAccounts(banks);
      setExpenseAccounts(expenses);
      if (companySettings?.qbPurchaseAccountId) {
        setQbPurchaseAccountId(String(companySettings.qbPurchaseAccountId));
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load this receipt.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanId]);

  const ingredientAccountMap = useMemo(
    () => Object.fromEntries(ingredientAccounts.map(i => [i.id, i])),
    [ingredientAccounts]
  );

  const excludedLineIndexes = (scan?.confirmedLineItems || [])
    .map((l, idx) => ({ ...l, idx }))
    .filter(l => l.ingredientId == null);

  // Ingredient-linked lines that won't resolve to a real QBO account today — the exact same
  // fallback chain the backend uses (ingredient's own account, else the company default), minus
  // whatever the user has already picked in this modal but not yet saved.
  const ingredientLinesNeedingAccount = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const l of (scan?.confirmedLineItems || [])) {
      if (l.ingredientId == null || seen.has(l.ingredientId)) continue;
      const acct = ingredientAccountMap[l.ingredientId];
      const resolved = ingredientOverrides[l.ingredientId] || acct?.qbExpenseAccountId || defaultAccountOverride || company?.qbDefaultExpenseAccountId;
      if (!resolved) {
        seen.add(l.ingredientId);
        out.push({ ingredientId: l.ingredientId, name: acct?.name || l.description });
      }
    }
    return out;
  }, [scan, ingredientAccountMap, ingredientOverrides, defaultAccountOverride, company]);

  const taxAmount = scan?.confirmedTaxAmount || 0;
  const taxNeedsAccount = taxAmount > 0 && !salesTaxOverride && !company?.qbSalesTaxAccountId;
  const noCompanyDefaultSet = !company?.qbDefaultExpenseAccountId && !defaultAccountOverride;

  const handleSaveDefaultAccount = async () => {
    if (!defaultAccountOverride) return;
    setSavingKey('default');
    try {
      const name = expenseAccounts.find(a => String(a.id) === String(defaultAccountOverride))?.name || null;
      await companyService.updateSettings({ qbDefaultExpenseAccountId: defaultAccountOverride, qbDefaultExpenseAccountName: name });
      setCompany(c => ({ ...c, qbDefaultExpenseAccountId: defaultAccountOverride, qbDefaultExpenseAccountName: name }));
      refreshPermissions?.();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to save the default expense account.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveIngredientAccount = async (ingredientId) => {
    const accountId = ingredientOverrides[ingredientId];
    if (!accountId) return;
    setSavingKey(`ingredient-${ingredientId}`);
    try {
      const name = expenseAccounts.find(a => String(a.id) === String(accountId))?.name || null;
      await qboService.applyIngredientAccounts({ link: [{ ingredientId, qbExpenseAccountId: accountId, qbExpenseAccountName: name }] });
      setIngredientAccounts(prev => {
        const next = prev.filter(i => i.id !== ingredientId);
        next.push({ id: ingredientId, name: prev.find(i => i.id === ingredientId)?.name, qbExpenseAccountId: accountId, qbExpenseAccountName: name });
        return next;
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to save this ingredient’s account.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveSalesTaxAccount = async () => {
    if (!salesTaxOverride) return;
    setSavingKey('tax');
    try {
      const name = expenseAccounts.find(a => String(a.id) === String(salesTaxOverride))?.name || null;
      await companyService.updateSettings({ qbSalesTaxAccountId: salesTaxOverride, qbSalesTaxAccountName: name });
      setCompany(c => ({ ...c, qbSalesTaxAccountId: salesTaxOverride, qbSalesTaxAccountName: name }));
      refreshPermissions?.();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to save the Sales Tax account.');
    } finally {
      setSavingKey(null);
    }
  };

  const handlePush = async () => {
    if (!qbPurchaseAccountId) { setError('Select a purchase account.'); return; }
    setError('');
    setPushing(true);
    try {
      const selectedBankAccount = bankAccounts.find(a => String(a.id) === String(qbPurchaseAccountId));
      const lineAccountOverrides = {};
      for (const [idx, accountId] of Object.entries(overrides)) {
        if (accountId) lineAccountOverrides[idx] = accountId;
      }
      // Ingredient-line overrides aren't keyed by line index on the backend (an ingredient can
      // appear on more than one line) — resolve each confirmedLineItem's index from its
      // ingredientId so a picked-but-not-yet-saved override still applies to this one push.
      (scan?.confirmedLineItems || []).forEach((l, idx) => {
        if (l.ingredientId != null && ingredientOverrides[l.ingredientId]) {
          lineAccountOverrides[idx] = ingredientOverrides[l.ingredientId];
        } else if (l.ingredientId != null && defaultAccountOverride && !ingredientAccountMap[l.ingredientId]?.qbExpenseAccountId) {
          lineAccountOverrides[idx] = defaultAccountOverride;
        }
      });
      const result = await receiptScanService.pushToQbo(scanId, {
        qbPurchaseAccountId,
        qbPurchaseAccountSubType: selectedBankAccount?.subType || null,
        lineAccountOverrides,
        salesTaxAccountOverride: salesTaxOverride || undefined,
      });
      setPushResult(result);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to push to QuickBooks.');
    } finally {
      setPushing(false);
    }
  };

  const handleClose = () => {
    if (pushResult) onPushed();
    onClose();
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">📤 Push Receipt to QuickBooks</h5>
            <button type="button" className="btn-close" onClick={handleClose} disabled={pushing} />
          </div>
          <div className="modal-body">
            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" />
              </div>
            )}

            {!loading && error && <div className="alert alert-danger py-2">{error}</div>}

            {!loading && scan && !pushResult && (
              <>
                <div className="mb-3 small text-muted">
                  {scan.extractedSupplierName && <div><strong>Supplier:</strong> {scan.extractedSupplierName}</div>}
                  {scan.confirmedDeliveryDate && <div><strong>Date:</strong> {scan.confirmedDeliveryDate}</div>}
                  <div><strong>Lines:</strong> {(scan.confirmedLineItems || []).length}</div>
                </div>

                <div className="mb-3">
                  <label className="form-label small mb-1">Purchase Account (card/bank this was paid with) <span className="text-danger">*</span></label>
                  <SearchableSelect
                    options={bankAccounts.map(a => ({ value: a.id, label: a.name }))}
                    value={qbPurchaseAccountId}
                    onChange={setQbPurchaseAccountId}
                    placeholder="Select an account…"
                    size="sm"
                  />
                </div>

                {(ingredientLinesNeedingAccount.length > 0 || taxNeedsAccount) && (
                  <div className="border border-warning rounded p-2 mb-3" style={{ background: '#fff9e6' }}>
                    <div className="small fw-semibold mb-2">⚠ Some lines need a QuickBooks expense account before this can push</div>

                    {noCompanyDefaultSet && ingredientLinesNeedingAccount.length > 0 && (
                      <div className="mb-3 pb-2 border-bottom">
                        <div className="small mb-1">
                          Fastest fix: set a <strong>company default</strong> — used for any ingredient with no
                          account of its own (today and on future receipts).
                        </div>
                        <div className="d-flex gap-2 align-items-start">
                          <div className="flex-fill">
                            <SearchableSelect
                              options={expenseAccounts.map(a => ({ value: a.id, label: a.name }))}
                              value={defaultAccountOverride}
                              onChange={setDefaultAccountOverride}
                              placeholder="Default expense account…"
                              size="sm"
                            />
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            disabled={!defaultAccountOverride || savingKey === 'default'}
                            onClick={handleSaveDefaultAccount}
                          >
                            {savingKey === 'default' ? 'Saving…' : '💾 Save'}
                          </button>
                        </div>
                      </div>
                    )}

                    {ingredientLinesNeedingAccount.length > 0 && (
                      <div className={taxNeedsAccount ? 'mb-3' : ''}>
                        <div className="small fw-semibold mb-1">Or set a specific account per ingredient:</div>
                        {ingredientLinesNeedingAccount.map(l => (
                          <div key={l.ingredientId} className="mb-2">
                            <div className="small">{l.name}</div>
                            <div className="d-flex gap-2 align-items-start">
                              <div className="flex-fill">
                                <SearchableSelect
                                  options={expenseAccounts.map(a => ({ value: a.id, label: a.name }))}
                                  value={ingredientOverrides[l.ingredientId] || ''}
                                  onChange={v => setIngredientOverrides(prev => ({ ...prev, [l.ingredientId]: v }))}
                                  placeholder="Expense account…"
                                  size="sm"
                                />
                              </div>
                              <button
                                className="btn btn-sm btn-outline-primary text-nowrap"
                                disabled={!ingredientOverrides[l.ingredientId] || savingKey === `ingredient-${l.ingredientId}`}
                                onClick={() => handleSaveIngredientAccount(l.ingredientId)}
                              >
                                {savingKey === `ingredient-${l.ingredientId}` ? 'Saving…' : '💾 Save to ingredient'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {taxNeedsAccount && (
                      <div>
                        <div className="small">Sales Tax — ${Number(taxAmount).toFixed(2)}</div>
                        <div className="d-flex gap-2 align-items-start">
                          <div className="flex-fill">
                            <SearchableSelect
                              options={expenseAccounts.map(a => ({ value: a.id, label: a.name }))}
                              value={salesTaxOverride}
                              onChange={setSalesTaxOverride}
                              placeholder="Expense account…"
                              size="sm"
                            />
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            disabled={!salesTaxOverride || savingKey === 'tax'}
                            onClick={handleSaveSalesTaxAccount}
                          >
                            {savingKey === 'tax' ? 'Saving…' : '💾 Save'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {excludedLineIndexes.length > 0 && (
                  <div className="border rounded p-2 mb-3">
                    <div className="small fw-semibold mb-2">Non-ingredient lines on this receipt</div>
                    {excludedLineIndexes.map(l => (
                      <div key={l.idx} className="mb-2">
                        <div className="small">{l.description} — ${Number(l.amount).toFixed(2)}</div>
                        <SearchableSelect
                          options={expenseAccounts.map(a => ({ value: a.id, label: a.name }))}
                          value={overrides[l.idx] || ''}
                          onChange={v => setOverrides(prev => ({ ...prev, [l.idx]: v }))}
                          placeholder="Expense account (defaults to company default)…"
                          size="sm"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {pushResult && (
              <div className="text-center py-4">
                <div className="mb-2" style={{ fontSize: 32 }}>✅</div>
                <div className="text-success">Pushed to QuickBooks as Purchase #{pushResult.qboPurchaseId}</div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={handleClose} disabled={pushing}>
              {pushResult ? 'Close' : 'Cancel'}
            </button>
            {!pushResult && !loading && scan && (
              <button className="btn btn-primary" onClick={handlePush} disabled={pushing}>
                {pushing ? <><span className="spinner-border spinner-border-sm me-2" />Pushing…</> : 'Push to QuickBooks'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
